import React, { useState } from 'react';

interface Purchase {
  id: string;
  date: string;
  total: number;
  status: string;
  items: {
    id: string;
    name: string;
    quantity: number;
    price: number;
  }[];
}

interface VoucherSendModalProps {
  purchase: Purchase;
  onClose: () => void;
  onSuccess: () => void;
}

interface VoucherUnit {
  itemId: string;
  itemName: string;
  unitNumber: number;
  recipient: {
    name: string;
    email: string;
    phone: string;
    whatsapp: string;
  };
}

const VoucherSendModal: React.FC<VoucherSendModalProps> = ({ purchase, onClose, onSuccess }) => {
  // Criar lista de vouchers individuais
  const createVoucherUnits = (): VoucherUnit[] => {
    const units: VoucherUnit[] = [];
    purchase.items.forEach(item => {
      for (let i = 1; i <= item.quantity; i++) {
        units.push({
          itemId: item.id,
          itemName: item.name,
          unitNumber: i,
          recipient: {
            name: '',
            email: '',
            phone: '',
            whatsapp: ''
          }
        });
      }
    });
    return units;
  };

  const [voucherUnits, setVoucherUnits] = useState<VoucherUnit[]>(createVoucherUnits());
  const [selectedVouchers, setSelectedVouchers] = useState<number[]>([]);
  const [messageTemplate, setMessageTemplate] = useState(
    'Olá {nome}!\n\nParabéns! Você foi premiado com:\n{voucher_detalhes}\n\nAcesse seu voucher digital através do link:\n{link_voucher}\n\nAproveite!'
  );
  const [showPreview, setShowPreview] = useState(false);

  const handleRecipientChange = (index: number, field: keyof VoucherUnit['recipient'], value: string) => {
    const newUnits = [...voucherUnits];
    newUnits[index].recipient[field] = value;
    setVoucherUnits(newUnits);
  };

  const toggleVoucherSelection = (index: number) => {
    setSelectedVouchers(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleSelectAll = () => {
    if (selectedVouchers.length === voucherUnits.length) {
      setSelectedVouchers([]);
    } else {
      setSelectedVouchers(voucherUnits.map((_, index) => index));
    }
  };

  const handleSend = () => {
    // Validar vouchers selecionados
    const selectedUnits = voucherUnits.filter((_, index) => selectedVouchers.includes(index));
    
    const hasEmptyFields = selectedUnits.some(unit => 
      !unit.recipient.name.trim() || !unit.recipient.email.trim()
    );

    if (selectedVouchers.length === 0) {
      alert('Selecione pelo menos um voucher para enviar!');
      return;
    }

    if (hasEmptyFields) {
      alert('Preencha Nome e Email para todos os vouchers selecionados!');
      return;
    }

    console.log('Enviando vouchers:', selectedUnits);
    onSuccess();
  };

  const getPreviewMessage = (unit: VoucherUnit) => {
    return messageTemplate
      .replace('{nome}', unit.recipient.name || '[Nome do Premiado]')
      .replace('{voucher_detalhes}', unit.itemName)
      .replace('{link_voucher}', 'https://voucher.exemplo.com/abc123');
  };

  // Agrupar por tipo para mostrar resumo
  const voucherSummary = purchase.items.map(item => ({
    name: item.name,
    quantity: item.quantity,
    selected: voucherUnits.filter((v, i) => v.itemId === item.id && selectedVouchers.includes(i)).length
  }));

  return (
    <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-gray-900">
              <i className="ri-send-plane-line mr-2 text-blue-600"></i>
              Enviar Vouchers - Pedido {purchase.id}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          
          {/* Resumo */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-900 mb-2">Resumo do Pedido:</p>
                {voucherSummary.map((item, index) => (
                  <p key={index} className="text-sm text-blue-800">
                    • {item.name} - {item.selected}/{item.quantity} selecionado(s)
                  </p>
                ))}
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-700">Total de Vouchers</p>
                <p className="text-2xl font-bold text-blue-900">{selectedVouchers.length}/{voucherUnits.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lista de Vouchers */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Vouchers Individuais
                </h3>
                <button
                  onClick={toggleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium cursor-pointer whitespace-nowrap"
                >
                  {selectedVouchers.length === voucherUnits.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </button>
              </div>

              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                {voucherUnits.map((unit, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 transition-all ${
                      selectedVouchers.includes(index)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    {/* Checkbox e Nome do Voucher */}
                    <div className="flex items-start gap-3 mb-3">
                      <input
                        type="checkbox"
                        checked={selectedVouchers.includes(index)}
                        onChange={() => toggleVoucherSelection(index)}
                        className="mt-1 w-4 h-4 text-blue-600 cursor-pointer"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">
                          {unit.itemName}
                        </p>
                        <p className="text-xs text-gray-500">
                          Unidade {unit.unitNumber} de {purchase.items.find(i => i.id === unit.itemId)?.quantity}
                        </p>
                      </div>
                    </div>

                    {/* Campos do Destinatário */}
                    {selectedVouchers.includes(index) && (
                      <div className="space-y-2 pl-7">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Nome do Premiado *
                          </label>
                          <input
                            type="text"
                            value={unit.recipient.name}
                            onChange={(e) => handleRecipientChange(index, 'name', e.target.value)}
                            placeholder="Digite o nome completo"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Email *
                          </label>
                          <input
                            type="email"
                            value={unit.recipient.email}
                            onChange={(e) => handleRecipientChange(index, 'email', e.target.value)}
                            placeholder="email@exemplo.com"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Telefone (SMS)
                            </label>
                            <input
                              type="tel"
                              value={unit.recipient.phone}
                              onChange={(e) => handleRecipientChange(index, 'phone', e.target.value)}
                              placeholder="(11) 98765-4321"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              WhatsApp
                            </label>
                            <input
                              type="tel"
                              value={unit.recipient.whatsapp}
                              onChange={(e) => handleRecipientChange(index, 'whatsapp', e.target.value)}
                              placeholder="(11) 98765-4321"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Template e Preview */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Template da Mensagem
              </h3>

              <div className="space-y-4">
                {/* Editor de Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personalize sua mensagem
                  </label>
                  <textarea
                    value={messageTemplate}
                    onChange={(e) => setMessageTemplate(e.target.value)}
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Digite sua mensagem..."
                  />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      <code>{'{nome}'}</code> - Nome do premiado
                    </span>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      <code>{'{voucher_detalhes}'}</code> - Detalhes do voucher
                    </span>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      <code>{'{link_voucher}'}</code> - Link de acesso
                    </span>
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 mb-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`ri-eye-${showPreview ? 'off-' : ''}line`}></i>
                    {showPreview ? 'Ocultar' : 'Visualizar'} Preview
                  </button>

                  {showPreview && selectedVouchers.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-[300px] overflow-y-auto">
                      <p className="text-xs font-semibold text-gray-700 mb-3">
                        Preview da primeira mensagem:
                      </p>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">
                          {getPreviewMessage(voucherUnits[selectedVouchers[0]])}
                        </pre>
                      </div>
                      {selectedVouchers.length > 1 && (
                        <p className="text-xs text-gray-500 mt-2">
                          + {selectedVouchers.length - 1} mensagem(ns) personalizada(s)
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Canais de Envio */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">
                    Canais de Envio:
                  </p>
                  <div className="space-y-1">
                    <p className="text-sm text-blue-800">
                      <i className="ri-mail-line mr-2"></i>
                      Email (obrigatório)
                    </p>
                    <p className="text-sm text-blue-800">
                      <i className="ri-message-3-line mr-2"></i>
                      SMS (se telefone preenchido)
                    </p>
                    <p className="text-sm text-blue-800">
                      <i className="ri-whatsapp-line mr-2"></i>
                      WhatsApp (se número preenchido)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {selectedVouchers.length} voucher(s) selecionado(s) para envio
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSend}
                disabled={selectedVouchers.length === 0}
                className={`px-6 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  selectedVouchers.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <i className="ri-send-plane-line mr-2"></i>
                Enviar {selectedVouchers.length > 0 ? `${selectedVouchers.length} ` : ''}Voucher(s)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherSendModal;
